import useRouteLoading from '@/hooks/useRouteLoading'
import axios, { type InternalAxiosRequestConfig } from 'axios'
import { useTokenStore } from '@/stores/user'
import {
  AuthError,
  clearAuthSession,
  getAuthReturnUrl,
  getAuthVersion,
  isAuthenticationFailure,
  recoverAccessToken,
} from '@/utils/framework'
import router from '@/router'
import type { ToastServiceMethods } from 'primevue'
import { shouldRedirectBadGateway } from '@/api/adapter/badGatewayPolicy'
import {
  BadGatewayRecoveryTimeoutError,
  createBadGatewayRecovery,
} from '@/api/adapter/badGatewayRecovery'

enum BUSINESS_STATE {
  SUCCESS = 10200,
  CREATED_SUCCESS = 10201,
}

const BUSINESS_SUCCESS_STATES = new Set<number>([
  BUSINESS_STATE.SUCCESS,
  BUSINESS_STATE.CREATED_SUCCESS,
])

const DEFAULT_ERROR_MESSAGE = 'The request could not be completed. Please try again.'

type AuthRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean
  _authVersion?: number
  _badGatewayRetry?: boolean
  _finishBadGatewayRecovery?: (config: AuthRequestConfig) => void
}

const finishBadGatewayRecovery = (config: AuthRequestConfig) => {
  config._finishBadGatewayRecovery?.(config)
  delete config._finishBadGatewayRecovery
  delete config._badGatewayRetry
}

let authRedirect: Promise<unknown> | null = null

const redirectAfterAuthFailure = (error: unknown, version: number) => {
  if (
    version !== getAuthVersion() ||
    authRedirect ||
    router.currentRoute.value.name === 'passport'
  ) {
    return
  }

  const requiresLogin = isAuthenticationFailure(error)
  const redirect = getAuthReturnUrl(router.currentRoute.value.fullPath)
  if (requiresLogin) clearAuthSession()

  const redirectVersion = getAuthVersion()
  const query = { redirect, reason: requiresLogin ? 'login' : 'unavailable' }
  const fallback = () => {
    if (redirectVersion === getAuthVersion() && router.currentRoute.value.name !== 'passport') {
      location.replace(`${location.origin}/passport?${new URLSearchParams(query)}`)
    }
  }

  authRedirect = router
    .replace({
      name: 'passport',
      query,
    })
    .then((failure) => {
      if (failure) fallback()
    }, fallback)
    .finally(() => {
      authRedirect = null
    })

  // Navigation failures must not become unhandled rejections in parallel API requests.
  void authRedirect.catch(() => undefined)
}

const emailAdapter = (ctx: { value?: { toast: () => ToastServiceMethods } }) => {
  const { start, stop } = useRouteLoading()

  const showToast = (options: Parameters<ToastServiceMethods['add']>[0]) => {
    // AppContext installs toast after mount. Route beforeEnter may fail earlier.
    const toast = ctx.value?.toast
    if (!toast) {
      return
    }

    toast().add(options)
  }

  const showErrorToast = (detail?: unknown) => {
    const message = typeof detail === 'string' && detail.trim() ? detail : DEFAULT_ERROR_MESSAGE

    showToast({
      life: 3000,
      detail: message,
      severity: 'error',
      group: 'bl',
      summary: 'Error Message',
    })
  }

  const recoveryMessage = {
    severity: 'warn' as const,
    group: 'bl',
    summary: 'Service temporarily unavailable',
    detail: 'The service is temporarily unavailable. Retrying for up to 15 seconds.',
    closable: false,
  }
  const recoverBadGateway = createBadGatewayRecovery({
    getSessionVersion: getAuthVersion,
    onStart: () => showToast(recoveryMessage),
    onEnd: () => ctx.value?.toast().remove(recoveryMessage),
    onTimeout: () => {
      router.push({ name: 'status', query: { q: 502 } })
    },
  })

  const api = axios.create({
    timeout: 1000 * 60 * 10,
    baseURL: import.meta.env.VITE_API_PREFIX,
  })

  api.interceptors.request.use((config) => {
    const authConfig = config as AuthRequestConfig
    const publicRequest = config.ignoreAuth || /^\/?public\/survey(?:\/|$)/.test(config.url || '')
    config.ignoreAuth = publicRequest

    if (
      !publicRequest &&
      authConfig._authVersion !== undefined &&
      authConfig._authVersion !== getAuthVersion()
    ) {
      throw new AuthError('stale')
    }

    authConfig._authVersion = getAuthVersion()
    const tokenStore = useTokenStore()
    const token = tokenStore.getToken()

    if (config.showLoading) {
      start()
    }

    if (!publicRequest && tokenStore.hasValidToken()) {
      config.headers['Authorization'] = token
    } else {
      delete config.headers['Authorization']
    }

    return config
  })

  api.interceptors.response.use(
    (response) => {
      const { request, config, data: rawData } = response
      const authConfig = config as AuthRequestConfig
      finishBadGatewayRecovery(authConfig)

      if (config.showLoading) {
        stop()
      }

      if (!config.ignoreAuth && authConfig._authVersion !== getAuthVersion()) {
        return Promise.reject(new AuthError('stale'))
      }

      if (request.responseType === 'blob') {
        return rawData
      }

      const { message: canMessage, suppressErrorToast } = config
      const { code, data, message } = rawData

      if (BUSINESS_SUCCESS_STATES.has(code)) {
        if (canMessage && message) {
          showToast({
            life: 3000,
            detail: message,
            severity: 'success',
            group: 'bl',
            summary: 'Success Message',
          })
        }

        return data
      }

      if (!suppressErrorToast) {
        showErrorToast(message)
      }

      return Promise.reject(message)
    },

    async (err) => {
      const { data, status } = err.response || {}
      const originalConfig = err.config as AuthRequestConfig | undefined

      // A non-502 response hands control back to normal handling, including token recovery.
      if (originalConfig && err.response && status !== 502) finishBadGatewayRecovery(originalConfig)

      if (err.config?.showLoading) {
        stop()
      }

      if (axios.isCancel(err)) {
        return Promise.reject(err)
      }

      if (
        err instanceof AuthError ||
        (!originalConfig?.ignoreAuth &&
          originalConfig?._authVersion !== undefined &&
          originalConfig._authVersion !== getAuthVersion())
      ) {
        return Promise.reject(new AuthError('stale'))
      }

      if (isAuthenticationFailure(err) && originalConfig && !originalConfig.ignoreAuth) {
        const version = originalConfig._authVersion ?? getAuthVersion()

        if (originalConfig._retry || originalConfig.ignoreRefreshToken) {
          redirectAfterAuthFailure(new AuthError('required'), version)
          return Promise.reject(err)
        }

        originalConfig._retry = true

        try {
          await recoverAccessToken(originalConfig.headers['Authorization'])
          if (version !== getAuthVersion()) throw new AuthError('stale')

          return api(originalConfig)
        } catch (refreshError) {
          if (!(refreshError instanceof AuthError && refreshError.kind === 'stale')) {
            redirectAfterAuthFailure(refreshError, version)
          }

          return Promise.reject(refreshError)
        }
      }

      if (shouldRedirectBadGateway(status, originalConfig?.handleBadGatewayLocally)) {
        if (originalConfig?._badGatewayRetry || router.currentRoute.value.name === 'status') {
          return Promise.reject(err)
        }

        if (originalConfig?.method?.toLowerCase() === 'get') {
          try {
            return await recoverBadGateway(
              (timeout, signal, finishRecovery) =>
                api({
                  ...originalConfig,
                  _badGatewayRetry: true,
                  _finishBadGatewayRecovery: (retryConfig: AuthRequestConfig) => {
                    retryConfig.timeout = originalConfig.timeout
                    finishRecovery()
                  },
                  timeout: originalConfig.timeout
                    ? Math.min(originalConfig.timeout, timeout)
                    : timeout,
                  signal,
                } as AuthRequestConfig),
              originalConfig.signal,
            )
          } catch (error) {
            return Promise.reject(error instanceof BadGatewayRecoveryTimeoutError ? err : error)
          }
        }

        if (!originalConfig?.suppressErrorToast) {
          showErrorToast(
            'The operation result could not be confirmed. Please verify it before trying again.',
          )
        }

        return Promise.reject(err)
      }

      if (!err.config?.suppressErrorToast) {
        showErrorToast(data?.message)
      }

      return Promise.reject(err)
    },
  )

  return api
}

export default emailAdapter
